import type { OrgRole } from './org.js';
import { canManageTeam, canPostDocuments } from './org.js';

export const CASHBOOK_VIEW_MODES = ['day', 'month', 'year', 'multi'] as const;
export type CashbookViewModePermission = (typeof CASHBOOK_VIEW_MODES)[number];

export const CASHBOOK_EXPORT_TYPES = ['csv', 'xlsx', 'pdf'] as const;
export type CashbookExportPermission = (typeof CASHBOOK_EXPORT_TYPES)[number];

export type CashbookPermissionOverrides = {
  post?: boolean;
  import?: boolean;
  void?: boolean;
  analytics?: boolean;
  debts?: boolean;
  datePicker?: boolean;
  balanceColumn?: boolean;
  viewModes?: Partial<Record<CashbookViewModePermission, boolean>>;
  exports?: Partial<Record<CashbookExportPermission, boolean>>;
};

export type CashbookAccess = {
  post: boolean;
  import: boolean;
  void: boolean;
  analytics: boolean;
  debts: boolean;
  datePicker: boolean;
  balanceColumn: boolean;
  viewModes: Record<CashbookViewModePermission, boolean>;
  exports: Record<CashbookExportPermission, boolean>;
};

const ALL_VIEW_MODES: Record<CashbookViewModePermission, boolean> = {
  day: true,
  month: true,
  year: true,
  multi: true
};

const ALL_EXPORTS: Record<CashbookExportPermission, boolean> = {
  csv: true,
  xlsx: true,
  pdf: true
};

function parseBooleanMap<T extends string>(
  raw: unknown,
  allowed: readonly T[]
): Partial<Record<T, boolean>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const out: Partial<Record<T, boolean>> = {};
  for (const key of allowed) {
    if (typeof data[key] === 'boolean') out[key] = data[key] as boolean;
  }
  return Object.keys(out).length ? out : undefined;
}

export function parseCashbookPermissionOverrides(
  raw: unknown
): CashbookPermissionOverrides | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const overrides: CashbookPermissionOverrides = {};
  if (typeof data.post === 'boolean') overrides.post = data.post;
  if (typeof data.import === 'boolean') overrides.import = data.import;
  if (typeof data.void === 'boolean') overrides.void = data.void;
  if (typeof data.analytics === 'boolean') overrides.analytics = data.analytics;
  if (typeof data.debts === 'boolean') overrides.debts = data.debts;
  if (typeof data.datePicker === 'boolean') overrides.datePicker = data.datePicker;
  if (typeof data.balanceColumn === 'boolean') overrides.balanceColumn = data.balanceColumn;
  const viewModes = parseBooleanMap(data.viewModes, CASHBOOK_VIEW_MODES);
  const exports = parseBooleanMap(data.exports, CASHBOOK_EXPORT_TYPES);
  if (viewModes) overrides.viewModes = viewModes;
  if (exports) overrides.exports = exports;
  return Object.keys(overrides).length ? overrides : undefined;
}

function defaultCashbookUiForRole(_role: OrgRole): Pick<
  CashbookAccess,
  'analytics' | 'debts' | 'datePicker' | 'balanceColumn' | 'viewModes' | 'exports'
> {
  return {
    analytics: true,
    debts: true,
    datePicker: true,
    balanceColumn: true,
    viewModes: { ...ALL_VIEW_MODES },
    exports: { ...ALL_EXPORTS }
  };
}

/** Any org member with a role can view and export daily records. */
export function canViewCashbook(_role: OrgRole): boolean {
  return true;
}

export function canPostCashbook(role: OrgRole): boolean {
  return canPostDocuments(role);
}

export function canImportCashbook(role: OrgRole): boolean {
  return canManageTeam(role);
}

export function canVoidCashbook(role: OrgRole): boolean {
  return canManageTeam(role);
}

export function defaultCashbookAccessForRole(role: OrgRole): CashbookAccess {
  if (role === 'owner') {
    return {
      post: true,
      import: true,
      void: true,
      ...defaultCashbookUiForRole(role)
    };
  }
  return {
    post: canPostCashbook(role),
    import: canImportCashbook(role),
    void: canVoidCashbook(role),
    ...defaultCashbookUiForRole(role)
  };
}

export function resolveCashbookAccess(
  role: OrgRole,
  overrides?: CashbookPermissionOverrides | null
): CashbookAccess {
  if (role === 'owner') return defaultCashbookAccessForRole('owner');
  const defaults = defaultCashbookAccessForRole(role);
  if (!overrides) return defaults;
  return {
    post: overrides.post ?? defaults.post,
    import: overrides.import ?? defaults.import,
    void: overrides.void ?? defaults.void,
    analytics: overrides.analytics ?? defaults.analytics,
    debts: overrides.debts ?? defaults.debts,
    datePicker: overrides.datePicker ?? defaults.datePicker,
    balanceColumn: overrides.balanceColumn ?? defaults.balanceColumn,
    viewModes: { ...defaults.viewModes, ...overrides.viewModes },
    exports: { ...defaults.exports, ...overrides.exports }
  };
}

export function hasCashbookPermissionOverrides(
  overrides?: CashbookPermissionOverrides | null
): boolean {
  if (!overrides) return false;
  if (
    overrides.post !== undefined ||
    overrides.import !== undefined ||
    overrides.void !== undefined ||
    overrides.analytics !== undefined ||
    overrides.debts !== undefined ||
    overrides.datePicker !== undefined ||
    overrides.balanceColumn !== undefined
  ) {
    return true;
  }
  if (overrides.viewModes && Object.keys(overrides.viewModes).length > 0) return true;
  if (overrides.exports && Object.keys(overrides.exports).length > 0) return true;
  return false;
}

export function firstAllowedCashbookViewMode(
  access: CashbookAccess
): CashbookViewModePermission {
  return CASHBOOK_VIEW_MODES.find((mode) => access.viewModes[mode]) ?? 'day';
}

export function cashbookOverridesFromAccess(
  role: OrgRole,
  access: CashbookAccess
): CashbookPermissionOverrides {
  const defaults = defaultCashbookAccessForRole(role);
  const overrides: CashbookPermissionOverrides = {};
  if (access.post !== defaults.post) overrides.post = access.post;
  if (access.import !== defaults.import) overrides.import = access.import;
  if (access.void !== defaults.void) overrides.void = access.void;
  if (access.analytics !== defaults.analytics) overrides.analytics = access.analytics;
  if (access.debts !== defaults.debts) overrides.debts = access.debts;
  if (access.datePicker !== defaults.datePicker) overrides.datePicker = access.datePicker;
  if (access.balanceColumn !== defaults.balanceColumn) overrides.balanceColumn = access.balanceColumn;

  const viewModes: Partial<Record<CashbookViewModePermission, boolean>> = {};
  for (const mode of CASHBOOK_VIEW_MODES) {
    if (access.viewModes[mode] !== defaults.viewModes[mode]) viewModes[mode] = access.viewModes[mode];
  }
  if (Object.keys(viewModes).length) overrides.viewModes = viewModes;

  const exports: Partial<Record<CashbookExportPermission, boolean>> = {};
  for (const kind of CASHBOOK_EXPORT_TYPES) {
    if (access.exports[kind] !== defaults.exports[kind]) exports[kind] = access.exports[kind];
  }
  if (Object.keys(exports).length) overrides.exports = exports;

  return overrides;
}
